import { useCallback, useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  BadgeCheck,
  Check,
  ExternalLink,
  Loader2,
  Lock,
  ShieldCheck,
  Sparkles,
  TriangleAlert,
  Wallet,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  REGRA_DO_DOWNLOAD,
  ROTULO_COBRANCA,
  descreverDownloads,
  type TipoCobranca,
} from '@miolos/core';
import { api, type CatalogoPlanos, type EstadoLicenca, type Plano, type RespostaPedido } from '@/lib/api';
import { useAuth } from '@/auth/AuthContext';
import { cn } from '@/lib/utils';

/**
 * Os tons de enfeite dos cartões de plano, na ordem do catálogo.
 *
 * Um tom por plano, girando: a grade de preços é a tela onde a pessoa decide, e
 * é ela que mais se beneficia de cor. O tom entra no filete do alto, na borda, no
 * realce do fundo e nos tiques da lista — o texto continua no tom do produto.
 */
const TONS_DOS_PLANOS = [
  'var(--cor-alegre-3)',
  'var(--cor-alegre-4)',
  'var(--cor-alegre-1)',
  'var(--cor-alegre-5)',
  'var(--cor-alegre-2)',
];

export function Planos() {
  const { usuario } = useAuth();
  const idDoUsuario = usuario?.id ?? '';
  const [parametros, setParametros] = useSearchParams();

  const [catalogo, setCatalogo] = useState<CatalogoPlanos | null>(null);
  const [licenca, setLicenca] = useState<EstadoLicenca | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [pedidoEmAberto, setPedidoEmAberto] = useState<RespostaPedido | null>(null);
  const [processando, setProcessando] = useState<string | null>(null);
  const [confirmando, setConfirmando] = useState(false);
  const [sucesso, setSucesso] = useState<string | null>(null);
  /** Conferência automática do pagamento ao voltar do checkout. */
  const [conferindo, setConferindo] = useState(false);
  const [tentativas, setTentativas] = useState(0);

  const voltouDoCheckout = parametros.get('pagamento') === 'concluido';

  const carregar = useCallback(async () => {
    try {
      const lista = await api.listarPlanos();
      setCatalogo(lista);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar os planos.');
    }

    if (!idDoUsuario) {
      setLicenca(null);
      return;
    }
    try {
      setLicenca(await api.minhaLicenca());
    } catch {
      /* sem licença ainda: segue com o catálogo */
    }
  }, [idDoUsuario]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  /**
   * Voltou do checkout? Pergunta ao servidor se o pagamento já consta no
   * histórico do provedor e, se constar, a licença é liberada na hora.
   * Repetimos algumas vezes porque Pix cai em segundos e cartão às vezes demora.
   */
  useEffect(() => {
    if (!voltouDoCheckout || !idDoUsuario || sucesso) return;

    let ativo = true;
    let rodada = 0;
    const MAX = 6;

    const conferir = async () => {
      rodada += 1;
      if (ativo) {
        setConferindo(true);
        setTentativas(rodada);
      }
      try {
        const pedidos = await api.listarPedidos();
        const pendente = pedidos.pedidos.find((item) => item.status !== 'PAGO');
        const alvo = pendente ?? pedidos.pedidos[0];
        if (!alvo) return;

        const resultado = await api.verificarPagamento(alvo.nsu);
        if (!ativo) return;

        if (resultado.situacao === 'PAGO') {
          setSucesso(
            `Pagamento confirmado pelo ${catalogo?.provedor.nome ?? 'provedor'}! Licença ${alvo.planoNome ?? alvo.planoId} ativada.`,
          );
          setParametros({}, { replace: true });
          await carregar();
          return;
        }
      } catch {
        /* rede instável: a próxima rodada tenta de novo */
      } finally {
        if (ativo) setConferindo(false);
      }

      if (ativo && rodada < MAX) {
        setTimeout(() => void conferir(), 6000);
      }
    };

    void conferir();

    return () => {
      ativo = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voltouDoCheckout, idDoUsuario, sucesso]);

  const assinar = async (plano: Plano) => {
    if (!usuario) return;
    setErro(null);
    setProcessando(plano.id);
    try {
      const resposta = await api.criarPedido(plano.id);
      setPedidoEmAberto(resposta);
      if (resposta.urlCheckout) {
        window.open(resposta.urlCheckout, '_blank', 'noopener,noreferrer');
      }
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível iniciar o pagamento.');
    } finally {
      setProcessando(null);
    }
  };

  const jaPaguei = async () => {
    if (!pedidoEmAberto) return;
    setConfirmando(true);
    setErro(null);
    try {
      await api.confirmarPedido(pedidoEmAberto.pedido.nsu);
      setSucesso(`Licença ${pedidoEmAberto.pedido.planoNome ?? ''} ativada. Bom trabalho!`);
      setPedidoEmAberto(null);
      setParametros({}, { replace: true });
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível confirmar o pagamento.');
    } finally {
      setConfirmando(false);
    }
  };

  return (
    <div className="space-y-8 py-4">
      <header className="mx-auto max-w-3xl space-y-3 text-center">
        <Badge tom="principal" className="mx-auto">
          <Wallet className="size-3" />
          Assinatura mensal · Pix ou cartão via {catalogo?.provedor.nome ?? 'InfinitePay'}
        </Badge>
        <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
          Escolha seu acesso ao Miolix
        </h1>
        {/*
         * O produto é **assinatura mensal** (não há plano anual, nem cobrança
         * única): o texto tem de dizer isso, porque é o que aparece na fatura. O
         * catálogo antigo anunciava "paga uma vez e usa" e o cartão repetia
         * "pagamento único" embaixo do preço — a promessa e a cobrança se
         * contradiziam na mesma tela.
         */}
        <p className="text-sm leading-relaxed text-muted-foreground">
          Assinaturas mensais, sem fidelidade: a cota de miolos se renova a cada ciclo e você cancela
          quando quiser — é só não renovar. A cobrança é no Pix ou no cartão, pelo{' '}
          {catalogo?.provedor.nome ?? 'InfinitePay'}.
        </p>

        {/*
          A regra que a pessoa precisa entender antes de pagar: a cota é de
          **miolos**, e cada miolo vale **um download**. Sem esta frase, quem compra
          "3 miolos" acha que pode baixar o mesmo arquivo três vezes.
        */}
        <p className="mx-auto max-w-2xl rounded-lg border border-primary/30 bg-primary/5 p-3 text-sm leading-relaxed">
          <strong className="font-semibold">{REGRA_DO_DOWNLOAD}</strong> Um plano de 3 miolos dá{' '}
          <strong>3 downloads</strong>: feitos os três, o plano é dado por encerrado.
        </p>
        {catalogo ? (
          <p className="text-xs text-muted-foreground">
            Pagamentos processados pelo <strong className="text-foreground">{catalogo.provedor.nome}</strong> na
            conta <span className="font-mono">{catalogo.provedor.tag}</span> · {catalogo.provedor.formas.join(' · ')}
          </p>
        ) : null}
      </header>

      {voltouDoCheckout && !sucesso ? (
        <p className="mx-auto flex max-w-2xl items-start gap-2 rounded-lg border border-border bg-muted/60 p-3 text-xs leading-relaxed text-muted-foreground">
          {conferindo ? (
            <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin" />
          ) : (
            <ShieldCheck className="mt-0.5 size-3.5 shrink-0 text-success" />
          )}
          <span>
            {conferindo ? (
              <>
                <strong className="text-foreground">Conferindo o pagamento</strong> no histórico do{' '}
                {catalogo?.provedor.nome ?? 'provedor'}… (tentativa {tentativas} de 6)
              </>
            ) : (
              <>
                Conferimos o seu pagamento automaticamente no histórico do {catalogo?.provedor.nome ?? 'provedor'}
                {' '}assim que ele é registrado — você não precisa fazer nada. Se o Pix ou o cartão já caiu e a licença
                ainda não apareceu, clique em “Já paguei” no plano escolhido ou aguarde a próxima verificação.
              </>
            )}
          </span>
        </p>
      ) : null}

      {sucesso ? (
        <p className="mx-auto flex max-w-2xl items-center gap-2 rounded-lg border border-emerald-300/70 bg-emerald-50 p-3 text-sm text-emerald-800 dark:border-emerald-500/40 dark:bg-emerald-500/10 dark:text-emerald-300">
          <BadgeCheck className="size-4 shrink-0" />
          {sucesso}
        </p>
      ) : null}

      {erro ? (
        <p className="mx-auto flex max-w-2xl items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          {erro}
        </p>
      ) : null}

      {licenca?.bloqueado ? (
        <p className="mx-auto flex max-w-2xl items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-xs text-destructive">
          <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
          <span>
            <strong className="font-semibold">Seu acesso está bloqueado.</strong> Seus miolos salvos continuam
            disponíveis para consulta, mas novos miolos só depois da liberação. Fale com quem te atende.
          </span>
        </p>
      ) : licenca?.licenca ? (
        <p className="mx-auto flex max-w-2xl flex-wrap items-center gap-2 rounded-lg border border-border bg-card p-3 text-sm">
          <BadgeCheck className="size-4 shrink-0 text-primary" />
          Sua licença atual: <strong>{licenca.licenca.plano.nome}</strong>
          {licenca.uso.limiteDownloads === null ? (
            <span className="text-muted-foreground">· downloads ilimitados</span>
          ) : licenca.uso.limiteDownloads === 0 ? (
            <span className="text-muted-foreground">· sem miolos de agenda (só documentos)</span>
          ) : (
            <span className="text-muted-foreground">
              · {licenca.uso.downloads} de {licenca.uso.limiteDownloads} downloads usados
            </span>
          )}
        </p>
      ) : null}

      {!catalogo ? (
        <div className="flex min-h-[30vh] items-center justify-center gap-3 text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          <span className="text-sm">Carregando planos...</span>
        </div>
      ) : (
        <div className="grid gap-5 lg:grid-cols-4">
          {catalogo.planos.map((plano, indice) => {
            const atual = licenca?.licenca?.planoId === plano.id;
            /*
             * Um tom por cartão, na ordem do catálogo: a grade de planos fica
             * colorida e cada plano tem a sua cor — sem inventar tom para plano
             * novo, que herda o próximo da roda.
             */
            const tom = TONS_DOS_PLANOS[indice % TONS_DOS_PLANOS.length]!;
            return (
              <Card
                key={plano.id}
                className={cn(
                  'cartao-tom relative flex flex-col transition-transform hover:-translate-y-0.5',
                  plano.destaque ? 'faixa-tom shadow-lg' : 'faixa-tom',
                )}
                style={{ '--tom': tom } as React.CSSProperties}
              >
                {plano.selo ? (
                  <span
                    className={cn(
                      'absolute -top-3 left-1/2 -translate-x-1/2 rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wide',
                      plano.destaque
                        ? 'painel-degrade shadow-md'
                        : 'bg-secondary text-secondary-foreground',
                    )}
                  >
                    {plano.selo}
                  </span>
                ) : null}

                <CardHeader className={cn(plano.selo && 'pt-7')}>
                  <CardTitle className="text-lg">{plano.nome}</CardTitle>
                  <CardDescription>{plano.resumo}</CardDescription>
                  <div className="flex items-baseline gap-2 pt-3">
                    <span className="text-3xl font-semibold tracking-tight">{plano.precoFormatado}</span>
                    {plano.precoDeFormatado ? (
                      <span className="text-sm text-muted-foreground line-through">
                        {plano.precoDeFormatado}
                      </span>
                    ) : null}
                  </div>
                  {/* A cobrança é a do próprio plano (mensal, único ou por miolo). */}
                  <p className="text-xs text-muted-foreground">{ROTULO_COBRANCA[plano.cobranca]}</p>
                  {/*
                    A cota em downloads, logo abaixo do preço: é o número que a
                    pessoa leva em conta ao comparar. Sem ele, "3 miolos" e "3
                    downloads" viram a mesma frase ambígua.
                  */}
                  <p className="pt-1 text-xs font-medium" style={{ color: tom }}>
                    {descreverDownloads(plano)}
                  </p>
                </CardHeader>

                <CardContent className="flex flex-1 flex-col gap-4">
                  <p className="rounded-md bg-muted/60 p-2.5 text-xs leading-relaxed text-muted-foreground">
                    {plano.publico}
                  </p>

                  <ul className="flex-1 space-y-2">
                    {plano.recursos.map((recurso) => (
                      <li key={recurso} className="flex gap-2 text-xs leading-relaxed">
                        <Check className="mt-0.5 size-3.5 shrink-0" strokeWidth={3} style={{ color: tom }} />
                        <span>{recurso}</span>
                      </li>
                    ))}
                  </ul>

                  {plano.precoCentavos <= 0 ? (
                    /*
                     * Plano gratuito não passa por checkout (a API recusa): o acesso
                     * sai na hora, na área do produto. O botão leva para lá.
                     */
                    <Button asChild variant="contorno" className="w-full">
                      <Link to="/escrever">
                        <Sparkles />
                        Acesso gratuito
                      </Link>
                    </Button>
                  ) : atual ? (
                    <Button variant="secundario" disabled className="w-full">
                      <BadgeCheck />
                      Seu plano atual
                    </Button>
                  ) : usuario ? (
                    <Button
                      variant={plano.destaque ? 'padrao' : 'contorno'}
                      className="w-full"
                      disabled={processando === plano.id}
                      onClick={() => void assinar(plano)}
                    >
                      {processando === plano.id ? <Loader2 className="animate-spin" /> : <Sparkles />}
                      Assinar {plano.nome}
                    </Button>
                  ) : (
                    <Button asChild variant={plano.destaque ? 'padrao' : 'contorno'} className="w-full">
                      <Link to="/criar-conta">
                        <Lock />
                        Criar conta para assinar
                      </Link>
                    </Button>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <section className="mx-auto max-w-3xl space-y-3 rounded-xl border border-border bg-card p-6">
        <h2 className="text-base font-semibold">Perguntas rápidas</h2>
        <dl className="space-y-3 text-xs leading-relaxed">
          <div>
            <dt className="font-medium text-foreground">Como o pagamento é confirmado?</dt>
            <dd className="text-muted-foreground">
              O {catalogo?.provedor.nome ?? 'provedor'} avisa nosso sistema assim que o pagamento cai e a licença é
              liberada na hora. Enquanto o webhook não estiver configurado, existe o botão “Já paguei”,
              pensado para testar o fluxo.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-foreground">A assinatura renova sozinha?</dt>
            <dd className="text-muted-foreground">
              Renova a cada ciclo de 30 dias, e a cota de miolos se renova junto. Sem fidelidade: para
              parar, é só não renovar — não existe multa nem aviso prévio.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-foreground">Posso vender as agendas que eu gerar?</dt>
            <dd className="text-muted-foreground">
              Os planos do site são de <strong className="text-foreground">uso pessoal</strong>. Para
              vender o que gerar (agendas impressas ou PDFs), fale com a gente — a licença comercial é
              combinada à parte.
            </dd>
          </div>
          <div>
            <dt className="font-medium text-foreground">O preço muda quando o catálogo mudar?</dt>
            <dd className="text-muted-foreground">
              Não. A sua assinatura continua no valor que você contratou enquanto estiver ativa.
            </dd>
          </div>
        </dl>
      </section>

      {/* Painel do pedido em aberto */}
      <Dialog open={Boolean(pedidoEmAberto)} onOpenChange={(aberto) => (aberto ? undefined : setPedidoEmAberto(null))}>
        {pedidoEmAberto ? (
          <DialogContent
            titulo="Pagamento em andamento"
            descricao={`Pedido ${pedidoEmAberto.pedido.nsu} · ${pedidoEmAberto.pedido.planoNome} · ${pedidoEmAberto.pedido.valorFormatado}`}
          >
            <div className="space-y-4">
              {pedidoEmAberto.urlCheckout ? (
                <Button asChild className="w-full">
                  <a href={pedidoEmAberto.urlCheckout} target="_blank" rel="noopener noreferrer">
                    <ExternalLink />
                    Abrir o checkout novamente
                  </a>
                </Button>
              ) : null}

              {pedidoEmAberto.aproximado && pedidoEmAberto.aviso ? (
                <p className="flex items-start gap-2 rounded-md border border-amber-300/70 bg-amber-50 p-3 text-xs leading-relaxed text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
                  <TriangleAlert className="mt-0.5 size-3.5 shrink-0" />
                  {pedidoEmAberto.aviso}
                </p>
              ) : null}

              {catalogo?.confirmacaoManual ? (
                <>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    Já concluiu o pagamento? Clique abaixo para liberar a licença imediatamente. Esta opção
                    existe para testes — em produção a liberação vem automática pelo webhook.
                  </p>
                  <Button variant="contorno" className="w-full" onClick={() => void jaPaguei()} disabled={confirmando}>
                    {confirmando ? <Loader2 className="animate-spin" /> : <BadgeCheck />}
                    Já paguei, liberar agora
                  </Button>
                </>
              ) : null}
            </div>
          </DialogContent>
        ) : null}
      </Dialog>
    </div>
  );
}
